import { parseErrorMessage } from "#src/util/helpers.js";

import { PrefillService } from "#src/services/prefill.service.js";
import { InstanceService } from "#src/services/instance.service.js";
import { QuotingService } from "#src/services/quoting.service.js";
import { QuotesService } from "#src/services/quotes.service.js";
import { EappsService } from "#src/services/eapps.service.js";
import { FlowService } from "#src/services/flow.service.js";

import {
  useJointInsuredStore,
  usePrimaryInsuredStore,
} from "#src/stores/insured.js";
import { steps } from "#src/enumerations/step-enumeration.js";

export class EappsController {
  #pinia;
  #prefillService;
  #instanceService;
  #quotingService;
  #quotesService;
  #eappsService;
  #flowService;

  constructor(pinia) {
    this.#pinia = pinia;
    this.#instanceService = new InstanceService(pinia);
    this.#prefillService = new PrefillService(pinia);
    this.#quotingService = new QuotingService(pinia);
    this.#quotesService = new QuotesService(pinia);
    this.#eappsService = new EappsService(pinia);
    this.#flowService = new FlowService(pinia);
  }

  async prefillQuoteAndApply(params, { fromAPI = false }) {
    const returnData = {
      uuid: null,
      apply_link: null,
      errors: null,
      warnings: null,
    };

    this.#instanceService.storePrefillData(params);

    let prefillParams = { ...params };
    if (!fromAPI) prefillParams.insured = { ...params };

    let createdEapp;
    try {
      if (fromAPI) createdEapp = await this.#apiCreate(prefillParams);
      else createdEapp = await this.#createFromPrefillQuery(prefillParams);
    } catch (e) {
      returnData.errors = e;
      return returnData;
    }

    returnData.apply_link = createdEapp.apply_link;
    returnData.uuid = createdEapp.uuid;

    try {
      await this.#prefillService.postCreatePrefill(prefillParams, {
        skipSetInsuredValues: !fromAPI,
        canChangeStep: true,
      });
    } catch (e) {
      returnData.warnings = e;
    }

    return returnData;
  }

  async updateEappsQuote(params) {
    this.#instanceService.initialize();
    const uuid = params?.uuid;
    const quoteId = params?.insured?.quote_id;
    const jointQuoteId = params?.joint_insured?.quote_id;
    if (!quoteId && !jointQuoteId)
      return { errors: { error: ["Missing required params"] } };

    try {
      await this.#eappsService.fetchEapp({ uuid });
    } catch (e) {
      return { errors: e?.errors };
    }

    if (quoteId) {
      try {
        const insured = usePrimaryInsuredStore(this.#pinia);
        await this.#quotesService.setActiveQuote(quoteId, insured.id);
      } catch (e) {
        return { errors: { error: [parseErrorMessage(e)] } };
      }
    }

    if (jointQuoteId) {
      const insured = useJointInsuredStore(this.#pinia);
      if (!insured.id)
        return {
          errors: { error: ["This eApp does not have a joint insured."] },
        };
      try {
        await this.#quotesService.setActiveQuote(jointQuoteId, insured.id);
      } catch (e) {
        return { errors: { error: [parseErrorMessage(e)] } };
      }
    }

    let errors = null;

    try {
      await this.#prefillService.postCreatePrefill(
        { step: steps.QUOTE },
        { canChangeStep: true }
      );
    } catch (e) {
      errors = e;
    }

    return { errors };
  }

  async advanceStep(params) {
    this.#instanceService.initialize();
    const uuid = params?.uuid;
    if (!uuid) return { errors: { error: ["Missing required params"] } };

    try {
      const eapp = await this.#eappsService.fetchEapp({ uuid });
      this.#flowService.setStep(eapp.step);
    } catch (e) {
      return { errors: e?.errors };
    }

    let errors = null;
    let response = null;
    try {
      response = await this.#flowService.advanceStep();
    } catch (e) {
      errors = e;
    }

    return { errors, ...response };
  }

  async #createFromPrefillQuery(params) {
    this.#instanceService.initialize();
    await this.#prefillService.preCreatePrefill(params, {
      strictProductParams: false,
      strictInsuredParams: false,
      canChangeStep: true,
    });

    const { defaultQuote } = await this.#quotingService.getQuotesFromPrefill({
      strictParams: false,
    });
    // if step is quote or compare, set to quote
    // this.#flowService.setStep(steps.QUOTE);
    if (!defaultQuote) throw { error: ["No Quotes Found"] };
    return this.#eappsService.createEapp(defaultQuote);
  }

  async #apiCreate(params) {
    if (params?.insured?.quote_id) {
      this.#prefillService.preCreateEnvironmentPrefill(params, {
        includeQuoteData: false,
      });
      return await this.#createEappFromQuoteId(
        params.insured.quote_id,
        params?.joint_insured?.quote_id
      );
    }
    return await this.#createEappWithoutQuote(params);
  }

  async #createEappWithoutQuote(params) {
    try {
      await this.#instanceService.setQuoteAndApplyContext({
        query: params,
        isApi: true,
      });
    } catch (e) {
      throw { context: [parseErrorMessage(e)] };
    }

    this.#instanceService.initialize();
    this.#flowService.setStep(steps.NAME);
    await this.#prefillService.preCreatePrefill(params, {
      fromAPI: true,
      strictProductParams: false,
      strictInsuredParams: true,
    });

    return this.#eappsService.createEapp();
  }

  async #createEappFromQuoteId(quoteId, jointQuoteId) {
    this.#flowService.setStep(steps.QUOTE);
    return this.#eappsService.createEapp({
      id: quoteId,
      joint_insured_quote_id: jointQuoteId,
    });
  }
}
